import { isAdaAssetId } from "@sundae/cardano-utils";
import { useI18N } from "@sundae/react-hooks";
import { Balance, FlexList, FlexListItem, HoverCard, Percentage, Text } from "@sundae/ui-toolkit";
import { IAvatarProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Avatar/Avatar";
import { IHoverCardProps } from "@sundae/ui-toolkit/dist/types/components/organisms/HoverCard/HoverCard";
import { AssetAmount } from "@sundaeswap/asset";
import cx from "classnames";
import { FC, PropsWithChildren, useMemo } from "react";

import { CARDANO_EXPLORER_LINK_MAP } from "../../constants/cardano.constants";
import { PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import { useWalletPositions } from "../../gql/hooks/positions.query";
import { useProfileAppContext } from "../../stores";
import { getAssetName } from "../../utils/assets.utils";
import { divideBy } from "../../utils/number-format";
import { getPoolName } from "../../utils/pool.utils";
import AssetWithAssetName from "../AssetWithAssetName";
import CardanoExplorer from "../CardanoExplorer";

export interface IAssetAvatarProps extends Omit<IAvatarProps, "alt" | "src" | "pointer"> {
  pool?: PoolFragmentFragment;
  hoverCardProps?: IHoverCardProps;
}

const PoolHoverCard: FC<PropsWithChildren<IAssetAvatarProps>> = ({
  children,
  pool,
  hoverCardProps,
  ...avatarProps
}) => {
  const { currentLocale, t } = useI18N();
  const { positions } = useWalletPositions({
    page: 0,
    pageSize: 40, // Set it to a high number to fetch all positions.
  });
  const {
    state: { cardanoExplorer },
  } = useProfileAppContext();

  const position = useMemo(
    () => positions?.find((position) => position.pool.ident === pool?.ident),
    [positions]
  );

  const assetA = pool?.assetA;
  const assetB = pool?.assetB;
  const quantityA = useMemo(
    () => new AssetAmount(pool?.quantityA ?? 0, assetA?.decimals),
    [assetA, pool?.quantityA]
  );
  const quantityB = useMemo(
    () => new AssetAmount(pool?.quantityB ?? 0, assetB?.decimals),
    [assetB, pool?.quantityB]
  );

  if (!pool || !assetA || !assetB) return <>{children}</>;

  return (
    <HoverCard
      {...hoverCardProps}
      primitiveProps={{
        content: {
          ...hoverCardProps?.primitiveProps?.content,
          className: cx(
            "min-w-[17.5rem]",
            "md:min-w-[20rem]",
            hoverCardProps?.primitiveProps?.content?.className
          ),
        },
        trigger: {
          ...hoverCardProps?.primitiveProps?.trigger,
          asChild: true,
        },
      }}
      trigger={<div className="flex flex-wrap items-center gap-2">{children}</div>}
    >
      <div className="flex h-full w-full items-center gap-2">
        <div className="grid w-full">
          <div className="grid gap-2">
            <AssetWithAssetName
              {...avatarProps}
              assets={[assetA, assetB]}
              assetName={getPoolName({ assetA, assetB })}
              size="md"
            />
          </div>

          <div className="mt-3 w-full">
            <Text tag="h3">{t("liquidity", { ns: "liquidity", context: "information" })}</Text>

            <FlexList className="mt-2">
              <FlexListItem
                field={{ label: t("pool", { context: "ident", ns: "glossary" }), variant: "muted" }}
                value={{ label: pool.ident }}
              />
              <FlexListItem
                field={{ label: t("assetId", { ns: "assets" }), variant: "muted" }}
                value={{
                  label: (
                    <CardanoExplorer
                      hash={pool.assetLP.assetId}
                      size="sm"
                      type={CARDANO_EXPLORER_LINK_MAP.asset[cardanoExplorer]}
                    />
                  ),
                }}
              />
              <FlexListItem
                field={{ label: t("fee", { ns: "fees", context: "lp" }), variant: "muted" }}
                value={{
                  label: (
                    <Percentage
                      locale={currentLocale}
                      size="sm"
                      value={divideBy(pool.fee, 100) || 0}
                    />
                  ),
                }}
              />
              <FlexListItem
                field={{
                  label: t("poolQuantity", {
                    ns: "liquidity",
                    assetName: getAssetName(pool.assetA),
                  }),
                  variant: "muted",
                }}
                value={{
                  label: (
                    <Balance
                      locale={currentLocale}
                      showSymbol={isAdaAssetId(pool.assetA.assetId)}
                      size="sm"
                      value={quantityA}
                    />
                  ),
                }}
              />
              <FlexListItem
                field={{
                  label: t("poolQuantity", {
                    ns: "liquidity",
                    assetName: getAssetName(pool.assetB),
                  }),
                  variant: "muted",
                }}
                value={{
                  label: (
                    <Balance
                      locale={currentLocale}
                      showSymbol={isAdaAssetId(pool.assetB.assetId)}
                      size="sm"
                      value={quantityB}
                    />
                  ),
                }}
              />
              {position && (
                <FlexListItem
                  field={{
                    label: t("share", { ns: "common", context: "pool" }),
                    variant: "muted",
                  }}
                  value={{
                    label: (
                      <Percentage
                        formatOpts={{ minimumFractionDigits: 0, maximumFractionDigits: 4 }}
                        locale={currentLocale}
                        size="sm"
                        value={position.poolShare}
                      />
                    ),
                  }}
                />
              )}
            </FlexList>
          </div>
        </div>
      </div>
    </HoverCard>
  );
};

export default PoolHoverCard;
